import noop from "lodash/noop";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import {
  useManageRolesStore,
  useManageRolesStoreActions,
  useManageRolesStoreContext
} from "PFApp/stores/manage_roles";
import { UnsavedEdit } from "PFApp/stores/manage_roles/manage_roles_store.types";
import {
  ManageRolesFormType,
  ManageRolesMultiForm
} from "PFApp/types/activity_form/manage_roles_form_schema";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { updateActivity } from "PFCore/services/activities";
import { Id } from "PFCore/types";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { buildRoleMap, findRoleFormPath } from "../manage_roles.utils";
import { isEmptyValue } from "./use_form_unsaved_edits_tracker.utils";
import {
  buildRolePayload,
  getErrorLeafFieldName,
  getUnsavedEditsWithStatus,
  groupEditsByRole,
  matchErrorFieldNamesToKeys,
  resolveErrorFieldValue
} from "./use_unsaved_edits_sync.utils";

const DEBOUNCE_MS = 1000;
const MAX_WAIT_MS = 5000;

export const MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID = getGrowlId("manage-roles-sync-success");
export const MANAGE_ROLES_SYNC_ERROR_GROWL_ID = getGrowlId("manage-roles-sync-error");
const MANAGE_ROLES_REQUIRED_FIELD_GROWL_ID = getGrowlId("manage-roles-required-field");

type UseUnsavedEditsSync = {
  requiredKeysRef?: RefObject<Map<number, Set<string>>>;
};

export const useUnsavedEditsSync = ({ requiredKeysRef }: UseUnsavedEditsSync = {}) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles" });

  const { getValues, setValue } = useFormContext<ManageRolesMultiForm>();
  const { markInflight, markFailed, markKeysAsErrored, clearEdits, addUnsavedEdit } =
    useManageRolesStoreActions();
  const storeApi = useManageRolesStoreContext();
  const currentPage = useManageRolesStore((state) => state.currentPage);
  const { showGrowl } = useGrowlStoreActions();
  const { getAllErrorsByType } = useResponseErrors();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const maxWaitTimerRef = useRef<NodeJS.Timeout | null>(null);
  // scheduleSyncRef breaks a circular dependency: runSync calls scheduleSyncRef.current (to reschedule
  // after partial syncs) and scheduleSync calls runSync (to trigger the actual sync). Defining both as
  // useCallback would create a dependency cycle that React cannot resolve. The ref is updated each render
  // via the useEffect below so it always holds the latest scheduleSync without being a direct dependency.
  const scheduleSyncRef = useRef<() => void>(noop);
  const syncPausedRef = useRef<boolean>(false);

  const cancelTimers = useCallback(() => {
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (maxWaitTimerRef.current !== null) {
      clearTimeout(maxWaitTimerRef.current);
      maxWaitTimerRef.current = null;
    }
  }, []);

  const syncOneRole = useCallback(
    async (roleId: Id, keys: string[], edits: UnsavedEdit[], roleValues: ManageRolesFormType) => {
      const payload = buildRolePayload(roleValues, edits);

      if (Object.keys(payload).length <= 1 && "id" in payload) {
        clearEdits(keys);
        return;
      }

      try {
        await updateActivity(roleId, payload);
        clearEdits(keys);
      } catch (error) {
        markFailed(keys);

        const errors = getAllErrorsByType(error) ?? {};
        const errorFieldNames = Array.from(new Set(Object.keys(errors).map(getErrorLeafFieldName)));
        const { matchedKeys, unmatchedFieldNames } = matchErrorFieldNamesToKeys(errorFieldNames, keys);

        markKeysAsErrored(matchedKeys);

        unmatchedFieldNames.forEach((fieldName) => {
          addUnsavedEdit(
            roleId,
            fieldName,
            resolveErrorFieldValue(roleValues, fieldName),
            undefined,
            currentPage,
            "failed"
          );
        });

        throw new Error(`Role ${roleId} update failed`);
      }
    },
    [clearEdits, markFailed, markKeysAsErrored, addUnsavedEdit, currentPage, getAllErrorsByType]
  );

  const performSync = useCallback(async (): Promise<boolean> => {
    const unsavedOnly = getUnsavedEditsWithStatus(storeApi.getState().unsavedEdits, "unsaved");

    // Before marking inflight: check each unsaved edit for required fields that are now empty.
    // If a required field's current form value is empty, revert it to its previous value and
    // drop the edit — no API call should be triggered for an invalid empty value.
    const requiredKeysMap = requiredKeysRef?.current;

    if (requiredKeysMap && requiredKeysMap.size > 0) {
      const currentValues = getValues();
      const keysToRevert: string[] = [];

      Object.entries(unsavedOnly).forEach(([key, edit]) => {
        const path = findRoleFormPath(currentValues, edit.roleId);
        if (!path) {
          return;
        }

        const { subtemplateIndex, roleIndex } = path;
        const subtemplateRequiredKeys = requiredKeysMap.get(subtemplateIndex);

        if (subtemplateRequiredKeys?.has(edit.fieldName)) {
          const fullPath = `subtemplates.${subtemplateIndex}.roles.${roleIndex}.${edit.fieldName}` as const;
          const currentValue = getValues(fullPath as any);
          if (isEmptyValue(currentValue)) {
            setValue(fullPath as any, edit.previousValue as any);
            keysToRevert.push(key);
          }
        }
      });

      if (keysToRevert.length > 0) {
        clearEdits(keysToRevert);

        keysToRevert.forEach((key) => {
          delete unsavedOnly[key];
        });

        showGrowl({
          message: t("requiredFieldRevert"),
          kind: "error",
          id: MANAGE_ROLES_REQUIRED_FIELD_GROWL_ID,
          hidePreviousByTypeAndId: true
        });
      }
    }

    const editKeys = Object.keys(unsavedOnly);
    if (editKeys.length === 0) {
      return true;
    }

    markInflight(editKeys);

    const roleMap = buildRoleMap(getValues());
    const byRole = groupEditsByRole(unsavedOnly);
    const results = await Promise.allSettled(
      Array.from(byRole.entries()).map(([roleId, { keys, edits }]) => {
        const roleValues = roleMap.get(roleId);
        if (!roleValues) {
          clearEdits(keys);
          return Promise.resolve();
        }
        return syncOneRole(roleId, keys, edits, roleValues);
      })
    );

    const failuresCount = results.filter((result) => result.status === "rejected").length;
    const successesCount = byRole.size - failuresCount;

    if (successesCount > 0) {
      showGrowl({
        message: t("saveSuccess", { count: successesCount }),
        kind: "success",
        id: MANAGE_ROLES_SYNC_SUCCESS_GROWL_ID,
        hidePreviousByTypeAndId: true
      });
    }
    if (failuresCount > 0) {
      showGrowl({
        message: t("saveError"),
        kind: "alert",
        id: MANAGE_ROLES_SYNC_ERROR_GROWL_ID,
        hidePreviousByTypeAndId: true
      });
    }

    return failuresCount === 0;
  }, [clearEdits, getValues, showGrowl, markInflight, requiredKeysRef, setValue, storeApi, syncOneRole, t]);

  const runSync = useCallback(async () => {
    cancelTimers();
    await performSync();
    const stillUnsaved = Object.values(storeApi.getState().unsavedEdits).some(
      (edit) => edit.status === "unsaved"
    );
    if (stillUnsaved) {
      scheduleSyncRef.current();
    }
  }, [cancelTimers, performSync, storeApi]);

  const scheduleSync = useCallback(() => {
    if (syncPausedRef.current) {
      return;
    }
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(
      () => runSync().catch((err) => console.error("Unexpected sync error:", err)),
      DEBOUNCE_MS
    );
    if (maxWaitTimerRef.current === null) {
      maxWaitTimerRef.current = setTimeout(
        () => runSync().catch((err) => console.error("Unexpected sync error:", err)),
        MAX_WAIT_MS
      );
    }
  }, [runSync]);

  useEffect(() => {
    scheduleSyncRef.current = scheduleSync;
  }, [scheduleSync]);

  useEffect(() => {
    let prevUnsavedEdits = storeApi.getState().unsavedEdits;
    const unsubscribe = storeApi.subscribe((state) => {
      if (state.unsavedEdits !== prevUnsavedEdits) {
        prevUnsavedEdits = state.unsavedEdits;
        scheduleSync();
      }
    });
    return () => {
      unsubscribe();
      cancelTimers();
    };
  }, [cancelTimers, scheduleSync, storeApi]);

  const flushUnsavedEdits = useCallback(async (): Promise<boolean> => {
    cancelTimers();
    return performSync();
  }, [cancelTimers, performSync]);

  const pauseSync = useCallback(() => {
    cancelTimers();
    syncPausedRef.current = true;
  }, [cancelTimers]);

  const resumeSync = useCallback(() => {
    syncPausedRef.current = false;

    const hasUnsaved = Object.values(storeApi.getState().unsavedEdits).some(
      (edit) => edit.status === "unsaved"
    );

    if (hasUnsaved) {
      scheduleSync();
    }
  }, [scheduleSync, storeApi]);

  return { flushUnsavedEdits, pauseSync, resumeSync };
};
